import React, { useRef, useState } from 'react';

import { AjaxListingContext, InfinitePagination } from '$partials/base';
import { Button } from '$partials/ui';
import { IconClose, IconFilters } from '$partials/icon';
import { LayoutContext, LayoutProvider } from '$partials/global/layout';
import { useAjaxListing } from '$hooks/ajax-listing';
import { useMultiSelectFilter } from '$hooks/filters';
import FilterDrawer from '$partials/global/filter-drawer';
import { WorkListingItem } from '$partials/items';
import WorkGrid from '$partials/modules/blocks/work-grid';
import { Footer, Header } from '$partials/global';
import Radio from '$partials/ui/radio';
import TagCloud from '$partials/global/tag-cloud';

const shuffle = (array) => {
    let shuffle = [...array];
    // Create a shallow copy to keep the original array immutable
    for (let i = shuffle.length - 1; i > 0; i--) {
    // Pick a random index from 0 to i
        const j = Math.floor(Math.random() * (i + 1));

        // Swap elements using destructuring assignment
        [shuffle[i], shuffle[j]] = [shuffle[j], shuffle[i]];
    }

    return shuffle;
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
/**
 * @type {import('$types/components/pages.d.ts').WorkListingPage}
 */
const WorkListingPage = (props) => {
    const bodyRef = useRef();
    const page = props.data.page;
    const [sortOrder, setSortOrder] = useState(page.items.sortOrder || '-publication_date');
    const [seed, setSeed] = useState(new Date(Math.round(new Date().getTime() / (1000 * 60 * 2)) * (1000 * 60 * 2)).getTime());
    const [randomiseListing, setRandomiseListing] = useState(shuffle(page.items?.edges));

    const clientsFilter = useMultiSelectFilter({
        initialValue: page.items?.filters?.clients?.value,
        options: page.items?.filters?.clients?.options,
        facets: page.items?.facets?.find((f) => f.key === page.items?.filters?.clients?.facet)?.buckets
    });
    const coloursFilter = useMultiSelectFilter({
        initialValue: page.items?.filters?.colours?.value,
        options: page.items?.filters?.colours?.options,
        facets: page.items?.facets?.find((f) => f.key === page.items?.filters?.colours?.facet)?.buckets
    });
    const sectorsFilter = useMultiSelectFilter({
        initialValue: page.items?.filters?.sectors?.value,
        options: page.items?.filters?.sectors?.options,
        facets: page.items?.facets?.find((f) => f.key === page.items?.filters?.sectors?.facet)?.buckets
    });
    const servicesFilter = useMultiSelectFilter({
        initialValue: page.items?.filters?.services?.value,
        options: page.items?.filters?.services?.options,
        facets: page.items?.facets?.find((f) => f.key === page.items?.filters?.services?.facet)?.buckets
    });
    const subjectsFilter = useMultiSelectFilter({
        initialValue: page.items?.filters?.subjects?.value,
        options: page.items?.filters?.subjects?.options,
        facets: page.items?.facets?.find((f) => f.key === page.items?.filters?.subjects?.facet)?.buckets
    });
    const filterOptions = [
        {
            key: 'sectorsFilter',
            label: 'Sector',
            ...sectorsFilter
        },
        {
            key: 'subjectsFilter',
            label: 'Subject',
            ...subjectsFilter
        },
        {
            key: 'servicesFilter',
            label: 'Service',
            ...servicesFilter
        },
        {
            key: 'clientsFilter',
            label: 'Client',
            ...clientsFilter
        },
        {
            key: 'coloursFilter',
            label: 'Colour',
            ...coloursFilter
        }
    ];

    const listing = useAjaxListing({
        infinite: sortOrder !== 'random' ,
        items: page.items?.edges,
        path: `/api/v1/listings/${encodeURIComponent(props.data?.uri ?? '')}`,
        page: page.items?.page || 1,
        totalItems: page.items?.total,
        totalPages: page.items?.totalPages,
        query: {
            clients: clientsFilter.value,
            colours: coloursFilter.value,
            sectors: sectorsFilter.value,
            services: servicesFilter.value,
            subjects: subjectsFilter.value,
            sortOrder: sortOrder,
            ...(sortOrder === 'random' ? {seed: seed} : {} ),
        },
        onData: (data) => {
            setRandomiseListing([...randomiseListing, ...data.items]);
            clientsFilter.setFacets(data?.facets?.find((f) => f.key === page.items?.filters?.clients?.facet)?.buckets);
            coloursFilter.setFacets(data?.facets?.find((f) => f.key === page.items?.filters?.colours?.facet)?.buckets);
            sectorsFilter.setFacets(data?.facets?.find((f) => f.key === page.items?.filters?.sectors?.facet)?.buckets);
            servicesFilter.setFacets(data?.facets?.find((f) => f.key === page.items?.filters?.services?.facet)?.buckets);
            subjectsFilter.setFacets(data?.facets?.find((f) => f.key === page.items?.filters?.subjects?.facet)?.buckets);
        }
    });

    const clearFilters = () => {
        clientsFilter.setValue([]);
        coloursFilter.setValue([]);
        sectorsFilter.setValue([]);
        servicesFilter.setValue([]);
        subjectsFilter.setValue([]);
    };

    const chunkArray = (arr, chunkCount) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += chunkCount){
            chunks.push(arr.slice(i, i + chunkCount));
        }
        return chunks;
    };


    return (
        <LayoutProvider>
            <LayoutContext.Consumer>
                {({ showSidebar, setShowSidebar }) => {
                    return (
                        <div className={`${showSidebar ? 'overflow-x-hidden' : ''}`}>
                            <div className={`w-full flex flex-col flex-shrink-0 min-h-dvh filter-dialog-page ${showSidebar ? 'filter-dialog-page__open' : ''}`}>
                                <Header
                                    {...props.data.header}
                                    bodyRef={bodyRef}
                                    domain={props.domain}/>
                                <FilterDrawer
                                    handleClose={() => setShowSidebar(false)}
                                    isNavOpen={showSidebar}>
                                    <div className='px-8 sm:px-16 flex flex-col pb-48'>
                                        <div className='py-8 sm:py-16 w-full flex justify-between'>
                                            <p className='type-style-m'>
                                                {'Filters + Sort'}
                                            </p>
                                            <Button
                                                as='div'
                                                className='underline underline-offset-2 type-style-s'
                                                onClick={clearFilters}>
                                                {'Clear all'}
                                            </Button>
                                        </div>
                                        <div className='py-8 sm:py-16 grid gap-y-20 sm:gap-y-28'>
                                            {/* Sort */}
                                            <div className='flex flex-col gap-y-8'>
                                                <p className='pb-12 sm:pb-16 type-style-s'>
                                                    {'Sort'}
                                                </p>
                                                <Radio
                                                    checked={sortOrder === '-publication_date'}
                                                    label={'Latest'}
                                                    name='sort'
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSortOrder('-publication_date');
                                                        }
                                                    }}
                                                    value={'-publication_date'} />
                                                <Radio
                                                    checked={sortOrder === 'publication_date'}
                                                    label={'Oldest'}
                                                    name='sort'
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSortOrder('publication_date');
                                                        }
                                                    }}
                                                    value={'publication_date'} />
                                                <Radio
                                                    checked={sortOrder === 'random'}
                                                    label={'Random'}
                                                    name='sort'
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSortOrder('random');
                                                        }
                                                    }}
                                                    value={'random'} />
                                            </div>

                                            {/* Tag filters */}
                                            {filterOptions.map(cloud => {
                                                return (
                                                    <TagCloud
                                                        tagKey={cloud.key}
                                                        {...cloud} />
                                                );
                                            })}
                                            <div className='gap-4 flex-wrap flex'>
                                                {filterOptions.map(filter => {
                                                    if (filter.key === 'coloursFilter') { return null; }
                                                    return (
                                                        <>
                                                            {filter?.value?.map(value => {
                                                                const selected = filter.options.find(option => option.value === value);
                                                                if (!selected) {return null;}
                                                                return (
                                                                    <Button
                                                                        className='flex items-center button-filled'
                                                                        key={selected.id}
                                                                        onClick={() => {
                                                                            filter.toggleValue(selected.value);
                                                                        }}
                                                                        renderIcon={<IconClose/>}>
                                                                        {selected.label}
                                                                    </Button>
                                                                );
                                                            })}
                                                        </>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='fixed bottom-0 left-0 w-full bg-white p-8 sm:p-16 '>
                                        <Button
                                            className='flex items-center justify-center button-filled w-full mt-8'
                                            onClick={() => {
                                                const timestamp = new Date(Math.round(new Date().getTime() / (1000 * 60 * 2)) * (1000 * 60 * 2)).getTime();
                                                if (timestamp === seed) {
                                                    setRandomiseListing(shuffle(listing.items));
                                                }
                                                setSeed(timestamp);
                                                setShowSidebar(false);
                                            }}>
                                            {sortOrder === 'random' ? 'Show work' : `Show work (${listing.totalItems})`}
                                        </Button>
                                    </div>
                                </FilterDrawer>
                                <main id='main-content'>
                                    <div className='px-layout-outer pt-[60px] sm:pt-[100px] pb-32 sticky top-0 bg-[white] z-10'>
                                        <div className='-mx-layout-col flex'>
                                            <div className='px-layout-col flex flex-col w-full gap-y-16 sm:w-3/7'>
                                                <h1>
                                                    {page.title}
                                                </h1>
                                                <div
                                                    className='rich-text max-w-[400px]'
                                                    dangerouslySetInnerHTML={{ __html: page.standfirst }} />
                                                <Button
                                                    className='button-outline mt-16 flex items-center text-text-default-primary flex-row-reverse self-start'
                                                    onClick={() => setShowSidebar(true)}
                                                    renderIcon={<IconFilters className='mr-4 -ml-5 hover:text-text-default-invert'/>}>
                                                    Filters
                                                </Button>
                                                {(sectorsFilter.value.length > 0 || subjectsFilter.value?.length > 0 || servicesFilter.value?.length > 0 || clientsFilter.value?.length > 0 ) && (
                                                    <div className='gap-4 flex flex-wrap mt-16'>
                                                        {filterOptions.map(filter => {
                                                            return (
                                                                <>
                                                                    {filter?.value?.map(value => {
                                                                        const selected = filter.options.find(option => option.value === value);
                                                                        if (!selected) { return null; }
                                                                        return (
                                                                            <Button
                                                                                className='flex items-center button-filled'
                                                                                key={selected.id}
                                                                                onClick={() => {
                                                                                    filter.toggleValue(selected.value);
                                                                                }}
                                                                                renderIcon={<IconClose/>}>
                                                                                {selected.label}
                                                                            </Button>
                                                                        );
                                                                    })}
                                                                </>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <AjaxListingContext.Provider value={listing}>
                                        <div
                                            className='bg-[white] z-10 relative flex flex-wrap'
                                            ref={bodyRef}>
                                            {page.featuredWorks?.length > 0 && !(sectorsFilter.value.length > 0 || subjectsFilter.value?.length > 0 || servicesFilter.value?.length > 0 || clientsFilter.value?.length > 0 ) && sortOrder === '-publication_date' && page.featuredWorks.map(feature => {
                                                return (
                                                    <WorkGrid {...feature} />
                                                );
                                            })}
                                            {page.items?.edges?.length > 0 && chunkArray(sortOrder === 'random' ? randomiseListing : listing.items, 5).map((items, batch) => {
                                                const asset = items?.[0]?.node?.listingMedia?.video || items?.[0]?.node?.listingMedia?.image;
                                                const isLandscape = asset ? asset.width > asset.height : true;

                                                return (
                                                    <div
                                                        className='flex flex-wrap w-full'
                                                        key={`artist-listings-batch-${batch}`}>
                                                        {isLandscape && (
                                                            <div className='w-full'>
                                                                <WorkListingItem {...items[0].node}/>
                                                            </div>
                                                        )}

                                                        {batch % 2 === 0 && (
                                                            <>
                                                                <div className={'w-full sm:w-4/7'}>
                                                                    {items.map((itm, idx) => {
                                                                        if (!itm) { return null; }
                                                                        if (idx === 0 && isLandscape) {
                                                                            return null;
                                                                        }
                                                                        // 4/7 column should always have less items
                                                                        if (idx === items.length - 1 && items.length > 1 ) {
                                                                            return null;
                                                                        }
                                                                        if (idx % 2 === 0) {
                                                                            return (
                                                                                <div
                                                                                    key={itm.node.id}>
                                                                                    <WorkListingItem {...itm.node}/>
                                                                                </div>
                                                                            );
                                                                        }
                                                                    })}
                                                                </div>
                                                                <div className={'w-full sm:w-3/7'}>
                                                                    {items.map((itm, idx) => {
                                                                        if (!itm) { return null; }
                                                                        if (idx % 2 !== 0 || (idx === items.length - 1 && items.length > 1)) {
                                                                            return (
                                                                                <div
                                                                                    key={itm.node.id}>
                                                                                    <WorkListingItem {...itm.node}/>
                                                                                </div>
                                                                            );
                                                                        }
                                                                    })}
                                                                </div>
                                                            </>
                                                        )}

                                                        {/* Alt layout */}
                                                        {batch % 2 !== 0 && (
                                                            <div className={'flex flex-wrap w-full'}>
                                                                {items.map((itm, idx) => {
                                                                    if (!itm) { return null; }
                                                                    if (idx === 0 && isLandscape) {
                                                                        return null;
                                                                    }
                                                                    return (
                                                                        <div
                                                                            className={(isLandscape ? [2,3] : [1,2]).includes(idx) ? 'w-full sm:w-3/7': 'w-full sm:w-4/7'}
                                                                            key={itm.node.id}>
                                                                            <WorkListingItem {...itm.node}/>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            {/* No result */}
                                            {listing.items?.length === 0 && (
                                                <div className='px-layout-outer type-style-s pb-32'>
                                                    {'No results'}
                                                </div>
                                            )}
                                            <InfinitePagination />
                                        </div>
                                    </AjaxListingContext.Provider>
                                </main>
                                <Footer {...props.data?.footer}/>
                            </div>
                        </div>
                    );
                }}
            </LayoutContext.Consumer>
        </LayoutProvider>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default WorkListingPage;
